import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import useSubscription from '@src/hooks/useSubscription';
import Circle from '@src/sld/components/Circle/Circle';
import HorizontalLine from '@src/sld/components/Line/HorizontalLine';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import MultiSwitchConnector from '@src/sld/components/MultiSwitchConnector/MultiSwitchConnector';
import Resistor from '@src/sld/components/Resistor/Resistor';
import Switch from '@src/sld/components/Switch/Switch';
import { getDataPointCollectionByPointNames } from '@src/sld/utils';
import { DataPointWithValue } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import storeConnector from '@store/storeConnector';
import { ReactElement } from 'react';
import ArrowDown from '../components/ArrowDown/ArrowDown';
import BreakerBasic from '../components/Breaker/BreakerBasic';
import BreakerStack from './components/BreakerStack';
import Header from './components/Header';
import { BrazosBend_Device_Points_Config } from './devicePointsConfig';

type BrazosBendSLDProps = {
  siteMeta: SiteMeta;
};

function BrazosBend_Diagram(props: BrazosBendSLDProps): ReactElement {
  const { siteMeta } = props;
  let k010_Breaker_DataPoints: DataPointWithValue[] | undefined;
  let k100_Breaker_DataPoints: DataPointWithValue[] | undefined;
  let k200_Breaker_DataPoints: DataPointWithValue[] | undefined;
  let k300_Breaker_DataPoints: DataPointWithValue[] | undefined;
  let k011_Switch_DataPoints: DataPointWithValue[] | undefined;
  let k019_Switch_DataPoints: DataPointWithValue[] | undefined;

  const { data: meterWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'brazos_bend_sld_meter',
      {
        sourceDeviceId: siteMeta.siteId,
      },
      { enabled: true },
    );

  const meterDataPointsWithValues = useSubscription(
    meterWidgetConfig?.dataPoints || [],
  );

  if (meterDataPointsWithValues.length > 0) {
    k010_Breaker_DataPoints = getDataPointCollectionByPointNames(
      meterDataPointsWithValues.filter(
        (point) =>
          point.sourceDeviceId ===
          BrazosBend_Device_Points_Config.K010_BREAKER_POINTS.engineeringId,
      ),
      BrazosBend_Device_Points_Config.K010_BREAKER_POINTS.points.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    k100_Breaker_DataPoints = getDataPointCollectionByPointNames(
      meterDataPointsWithValues.filter(
        (point) =>
          point.sourceDeviceId ===
          BrazosBend_Device_Points_Config.K100_BREAKER_POINTS.engineeringId,
      ),
      BrazosBend_Device_Points_Config.K100_BREAKER_POINTS.points.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    k200_Breaker_DataPoints = getDataPointCollectionByPointNames(
      meterDataPointsWithValues.filter(
        (point) =>
          point.sourceDeviceId ===
          BrazosBend_Device_Points_Config.K200_BREAKER_POINTS.engineeringId,
      ),
      BrazosBend_Device_Points_Config.K200_BREAKER_POINTS.points.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    k300_Breaker_DataPoints = getDataPointCollectionByPointNames(
      meterDataPointsWithValues.filter(
        (point) =>
          point.sourceDeviceId ===
          BrazosBend_Device_Points_Config.K300_BREAKER_POINTS.engineeringId,
      ),
      BrazosBend_Device_Points_Config.K300_BREAKER_POINTS.points.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    k011_Switch_DataPoints = getDataPointCollectionByPointNames(
      meterDataPointsWithValues.filter(
        (point) =>
          point.sourceDeviceId ===
          BrazosBend_Device_Points_Config.k011_SWITCH_POINTS.engineeringId,
      ),
      BrazosBend_Device_Points_Config.k011_SWITCH_POINTS.points.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    k019_Switch_DataPoints = getDataPointCollectionByPointNames(
      meterDataPointsWithValues.filter(
        (point) =>
          point.sourceDeviceId ===
          BrazosBend_Device_Points_Config.k019_SWITCH_POINTS.engineeringId,
      ),
      BrazosBend_Device_Points_Config.k019_SWITCH_POINTS.points.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );
  }

  const getBreakerStatus = (
    sourceDeviceId: string,
    breakerStatusPointName: string,
    dataPoints: DataPointWithValue[] | undefined,
  ): boolean => {
    if (dataPoints) {
      const breakerDataPoints: DataPointWithValue[] = dataPoints.filter(
        (point) =>
          point.pointName === breakerStatusPointName &&
          point.sourceDeviceId === sourceDeviceId,
      );

      return breakerDataPoints.length > 0 && breakerDataPoints[0].value === 1;
    }

    return false;
  };

  const getSwitchStatus = (
    sourceDeviceId: string,
    switchStatusPointName: string,
    dataPoints: DataPointWithValue[] | undefined,
  ): boolean => {
    if (dataPoints) {
      const switchDataPoints: DataPointWithValue[] = dataPoints.filter(
        (point) =>
          point.pointName === switchStatusPointName &&
          point.sourceDeviceId === sourceDeviceId,
      );
      return switchDataPoints.length > 0 && switchDataPoints[0].value === 1;
    }

    return false;
  };

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='200 40 1200 700'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Brazos Bend SLD</title>
      <Header />

      <g transform='translate(743 145)'>
        <Switch
          isClosed={getSwitchStatus(
            BrazosBend_Device_Points_Config.k011_SWITCH_POINTS.engineeringId,
            'SUB_MOS_K011_SWITCH_STATUS',
            k011_Switch_DataPoints,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          label='K011'
          stroke={
            getSwitchStatus(
              BrazosBend_Device_Points_Config.k011_SWITCH_POINTS.engineeringId,
              'SUB_MOS_K011_SWITCH_STATUS',
              k011_Switch_DataPoints,
            )
              ? 'red'
              : 'green'
          }
        />

        <VerticalLine
          translateXCoordinate={0}
          translateYCoordinate={11}
          length={30}
          color='white'
          width={0.5}
        />
      </g>

      <g transform='translate(730 188)'>
        <BreakerBasic
          labels={[{ label: 'K010', xCoordinate: 40, yCoordinate: 10 }]}
          translateXCoordinate={0}
          translateYCoordinate={0}
          dataPointsWithValues={k010_Breaker_DataPoints}
          defaultHot={true}
        />
      </g>

      <g transform='translate(742 205)'>
        <VerticalLine
          translateXCoordinate={0}
          translateYCoordinate={11}
          length={40}
          color='white'
          width={0.5}
        />
      </g>

      {/* translate 170 = vertical position */}
      {/* translate -35 = horizontal position */}
      <g transform='rotate(90 700 783) translate(170 -35)'>
        <Resistor label={''} />
      </g>

      <g transform='translate(780 270)'>
        <text
          x={0}
          y={0}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          KT01
        </text>
      </g>

      <g transform='translate(778 283)'>
        <text
          x={0}
          y={0}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          XFMR
        </text>
      </g>

      <g transform='translate(778 300)'>
        <VerticalLine
          translateXCoordinate={-35}
          translateYCoordinate={-17}
          length={20}
          color='white'
          width={0.5}
        />

        <HorizontalLine
          translateXCoordinate={-40}
          translateYCoordinate={3}
          length={10}
          color='white'
          width={0.5}
        />

        <Circle
          translateXCoordinate={-35}
          translateYCoordinate={5}
          diameter={4}
          stroke='white'
          strokeWidth={2}
          label=''
        />

        <Switch
          isClosed={getSwitchStatus(
            BrazosBend_Device_Points_Config.k019_SWITCH_POINTS.engineeringId,
            'SUB_MOS_K019_SWITCH_STATUS',
            k019_Switch_DataPoints,
          )}
          translateXCoordinate={-34}
          translateYCoordinate={15}
          label='K019'
          stroke={
            getSwitchStatus(
              BrazosBend_Device_Points_Config.k019_SWITCH_POINTS.engineeringId,
              'SUB_MOS_K019_SWITCH_STATUS',
              k019_Switch_DataPoints,
            )
              ? 'red'
              : 'green'
          }
        />

        <VerticalLine
          translateXCoordinate={-34}
          translateYCoordinate={25}
          length={40}
          color='white'
          width={0.5}
        />
      </g>

      <g transform='translate(857 -357)'></g>

      <g transform='translate(500 -200)'>
        <MultiSwitchConnector
          translateXCoordinate={0}
          translateYCoordinate={0}
          horizontalLength={500}
          verticalLength={20}
          startingXCoordinate={0}
        />
      </g>

      <g transform='translate(612 -357)'>
        <BreakerStack
          breakerConnected={getBreakerStatus(
            BrazosBend_Device_Points_Config.K100_BREAKER_POINTS.engineeringId,
            'SUB_351_K100_BREAKER_STATUS',
            k100_Breaker_DataPoints,
          )}
          meterDataPoints={k100_Breaker_DataPoints}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'K100'}
          meterLabel={'K100'}
          meterDataPointsSectionXCoordinate={0}
          meterDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          AUXILIARY #1 AND #2 POWER FEEDER
        </text>
      </g>

      <g transform='translate(857 -357)'>
        <BreakerStack
          breakerConnected={getBreakerStatus(
            BrazosBend_Device_Points_Config.K200_BREAKER_POINTS.engineeringId,
            'SUB_351_K200_BREAKER_STATUS',
            k200_Breaker_DataPoints,
          )}
          meterDataPoints={k200_Breaker_DataPoints}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'K200'}
          meterLabel={'K200'}
          meterDataPointsSectionXCoordinate={0}
          meterDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          WEST LOOP #1
        </text>

        <HorizontalLine
          translateXCoordinate={-114}
          translateYCoordinate={770}
          length={60}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={770}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={-56} yCoordinate={796} />

        <text x={-65} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          WEST LOOP #2
        </text>
      </g>

      <g transform='translate(1113 -357)'>
        <BreakerStack
          breakerConnected={getBreakerStatus(
            BrazosBend_Device_Points_Config.K300_BREAKER_POINTS.engineeringId,
            'SUB_351_K300_BREAKER_STATUS',
            k300_Breaker_DataPoints,
          )}
          meterDataPoints={k300_Breaker_DataPoints}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'K300'}
          meterLabel={'K300'}
          meterDataPointsSectionXCoordinate={0}
          meterDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          EAST LOOP #1
        </text>

        <HorizontalLine
          translateXCoordinate={-114}
          translateYCoordinate={770}
          length={60}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={770}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={-56} yCoordinate={796} />

        <text x={-65} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          EAST LOOP #2
        </text>
      </g>
    </svg>
  );
}

export default storeConnector(BrazosBend_Diagram, {
  config: ['siteMeta'],
});
